import { useState, useEffect, useRef, ReactNode, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import socketService from '@/services/socket';
import { useToast } from '@/components/common/ToastProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionName } from '@/types/roles';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import ChannelSidebar from '@/components/channels/ChannelSidebar';
import RetentionPolicySettings from '@/components/admin/RetentionPolicySettings';

// Portal that anchors to a button and auto-positions vertically to avoid clipping
function DropdownPortal({
  anchorId,
  width = 320,
  children,
}: {
  anchorId: string;
  width?: number;
  children: ReactNode;
}) {
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>({
    position: 'fixed',
    left: 0,
    top: 0,
    width,
    zIndex: 9999,
    maxHeight: '60vh',
    overflowY: 'auto',
  });

  useEffect(() => {
    const anchor = document.getElementById(anchorId);
    const dropdown = portalRef.current;
    if (!anchor || !dropdown) return;

    const rect = anchor.getBoundingClientRect();
    const dropdownHeight = dropdown.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top = rect.bottom + 8; // default below
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      top = rect.top - dropdownHeight - 8; // place above if more space
    } else if (spaceBelow < dropdownHeight) {
      top = Math.max(8, window.innerHeight - dropdownHeight - 8); // fit within viewport
    }

    setStyle((prev) => ({
      ...prev,
      left: rect.right - width,
      top,
      width,
    }));
  }, [anchorId, width, children]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div ref={portalRef} style={style}>
      {children}
    </div>,
    document.body,
  );
}

interface User {
  id: string;
  username: string;
  email: string | null;
  status: string;
  isAdmin: boolean;
  isOwner: boolean;
  createdAt: string;
  lastSeenAt: number | null;
}

interface Channel {
  id: string;
  name: string;
  description: string | null;
  isPrivate: boolean;
  isVoice: boolean;
  position: number;
  retentionDays: number | null;
  createdAt: string;
}

interface Stats {
  totalUsers: number;
  onlineUsers: number;
  totalChannels: number;
  totalMessages: number;
  recentUsers: number;
}

interface Role {
  id: string;
  name: string;
  color: string;
  permissions: string[];
  position: number;
}

interface ChannelMember {
  id: string;
  userId: string;
  channelId: string;
  roleId: string;
  assignedAt: string;
  assignedBy: string | null;
  User: {
    id: string;
    username: string;
    email: string | null;
    status: string;
  };
  Role: Role;
}

interface InviteCode {
  id: string;
  code: string;
  type: string;
  status: string;
  expiresAt: string;
  createdBy: string;
  creator?: {
    username: string;
  };
}

export interface AdminPanelProps {
  embedded?: boolean;
  onExit?: () => void;
  onOpenSidebar?: () => void;
}

export default function AdminPanel({
  embedded = false,
  onExit,
  onOpenSidebar,
}: AdminPanelProps = {}) {
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'stats' | 'users' | 'invites' | 'channels' | 'services' | 'migration' | 'retention' | 'announcements'
  >('stats');
  const [users, setUsers] = useState<User[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [announcementType, setAnnouncementType] = useState<'info' | 'warning' | 'error'>('info');
  const [inviteExpiry, setInviteExpiry] = useState(24);
  const [generatedInvite, setGeneratedInvite] = useState<string>('');
  const [generatedInviteCode, setGeneratedInviteCode] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [channelMembers, setChannelMembers] = useState<ChannelMember[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDescription, setNewChannelDescription] = useState('');
  const [newChannelPrivate, setNewChannelPrivate] = useState(false);
  const [newChannelVoice, setNewChannelVoice] = useState(false);
  const [newChannelRetention, setNewChannelRetention] = useState<string>('');
  const [newChannelMembers, setNewChannelMembers] = useState<string[]>([]);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [editChannelName, setEditChannelName] = useState('');
  const [editChannelDescription, setEditChannelDescription] = useState('');
  const [editChannelPrivate, setEditChannelPrivate] = useState(false);
  const [editChannelVoice, setEditChannelVoice] = useState(false);
  const [editChannelRetention, setEditChannelRetention] = useState<string>('');
  const [savingChannel, setSavingChannel] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [announcementExpiry, setAnnouncementExpiry] = useState<string>('');
  const [enableExpiry, setEnableExpiry] = useState<boolean>(false);
  const [expiryMode, setExpiryMode] = useState<'absolute' | 'duration'>('duration');
  const [expiryDurationDays, setExpiryDurationDays] = useState<string>('7');
  const [expiryDurationHours, setExpiryDurationHours] = useState<string>('0');
  const [expiryDurationMinutes, setExpiryDurationMinutes] = useState<string>('0');
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);

  // Services & Server Settings state
  const [servicesOverview, setServicesOverview] = useState<any>(null);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serverSettings, setServerSettings] = useState<any>({
    serverName: 'Irus Chat',
    serverDescription: '',
    welcomeMessage: '',
    serverRules: '',
    maxFileSize: 25,
    enableFileUploads: true,
    enableFileEncryption: true,
    voiceBitrateMode: 'AUTO',
    voiceAutoAdjustBitrate: true,
    voiceNoiseSuppressionEnabled: true,
    voiceEchoCancellationEnabled: true,
    voiceAutoGainControlEnabled: true,
    voiceActivationMode: 'VOICE_ACTIVITY',
    passwordResetExpirationMinutes: 60,
    inviteCodeExpirationHours: 24,
    inviteCodeLockoutDurationMinutes: 30,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [importingData, setImportingData] = useState(false);
  const [downloadingSql, setDownloadingSql] = useState(false);
  const [restoringSql, setRestoringSql] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canGenerateInvites = hasPermission(PermissionName.EDIT_SERVER_SETTINGS);
  const { isMobile } = useBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Keyboard accessible tablist
  const tabListRef = useRef<HTMLDivElement | null>(null);

  const handleTabsKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const container = tabListRef.current;
    if (!container) return;
    const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('button[data-tab]'));
    if (buttons.length === 0) return;

    const activeEl = document.activeElement as HTMLElement;
    const idx = buttons.findIndex((b) => b === activeEl);
    if (idx === -1) {
      // No tab focused - focus current activeTab button
      const current = container.querySelector<HTMLButtonElement>(`button[data-tab="${activeTab}"]`);
      current?.focus();
      return;
    }

    const key = e.key || (e as any).code;
    switch (key) {
      case 'ArrowRight':
      case 'Right':
        e.preventDefault();
        buttons[(idx + 1) % buttons.length]?.focus();
        break;
      case 'ArrowLeft':
      case 'Left':
        e.preventDefault();
        buttons[(idx - 1 + buttons.length) % buttons.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        buttons[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        buttons[buttons.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
      case 'Space':
        e.preventDefault();
        (document.activeElement as HTMLButtonElement)?.click();
        break;
    }
  };

  // Check if user has any admin permissions
  const hasAnyAdminPermission =
    hasPermission(PermissionName.EDIT_SERVER_SETTINGS) ||
    hasPermission(PermissionName.MANAGE_USERS) ||
    hasPermission(PermissionName.MANAGE_CHANNEL_SETTINGS) ||
    hasPermission(PermissionName.MANAGE_RETENTION_POLICY);

  const hasRedirectedRef = useRef(false);

  // Redirect if user doesn't have admin permissions
  useEffect(() => {
    if (!permissionsLoading && !hasAnyAdminPermission && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      toast.error('You do not have permission to access the admin panel');
      navigate('/chat');
    }
  }, [permissionsLoading, hasAnyAdminPermission, navigate, toast]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'channels') {
      fetchChannels();
      fetchUsers();
      fetchRoles();
    } else if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'announcements') {
      fetchAnnouncements();
    } else if (activeTab === 'invites') {
      fetchInviteCodes();
    } else if (activeTab === 'services' || activeTab === 'migration') {
      fetchServicesOverview();
    }
  }, [activeTab]);

  // Listen for real-time status changes
  useEffect(() => {
    const handleStatusChanged = (data: any) => {
      if (!data?.userId) return;
      const status = data.status || 'offline';
      setUsers((prev) =>
        prev.map((u) => (String(u.id) === String(data.userId) ? { ...u, status } : u)),
      );
    };

    socketService.on('user:status-changed', handleStatusChanged);
    socketService.on('presence:user-online', handleStatusChanged);
    socketService.on('presence:user-offline', handleStatusChanged);
    socketService.on('user:online', handleStatusChanged);
    socketService.on('user:offline', handleStatusChanged);

    return () => {
      socketService.off('user:status-changed', handleStatusChanged);
      socketService.off('presence:user-online', handleStatusChanged);
      socketService.off('presence:user-offline', handleStatusChanged);
      socketService.off('user:online', handleStatusChanged);
      socketService.off('user:offline', handleStatusChanged);
    };
  }, []);

  useEffect(() => {
    const refreshChannels = () => {
      if (activeTab === 'channels') {
        fetchChannels();
      }
    };

    socketService.on('admin:channel-created', refreshChannels);
    socketService.on('admin:channels-reordered', refreshChannels);
    socketService.on('admin:channel-deleted', refreshChannels);
    socketService.on('admin:channel-updated', refreshChannels);

    return () => {
      socketService.off('admin:channel-created', refreshChannels);
      socketService.off('admin:channels-reordered', refreshChannels);
      socketService.off('admin:channel-deleted', refreshChannels);
      socketService.off('admin:channel-updated', refreshChannels);
    };
  }, [activeTab]);

  // Listen for announcement updates
  useEffect(() => {
    const handleAnnouncementCreated = (data: any) => {
      setAnnouncements((prev) => [data, ...prev]);
    };

    const handleAnnouncementRevoked = (data: { id: string }) => {
      setAnnouncements((prev) => prev.filter((a) => String(a.id) !== String(data.id)));
    };

    socketService.on('admin:announcement', handleAnnouncementCreated);
    socketService.on('admin:announcement:revoked', handleAnnouncementRevoked);

    return () => {
      socketService.off('admin:announcement', handleAnnouncementCreated);
      socketService.off('admin:announcement:revoked', handleAnnouncementRevoked);
    };
  }, []);

  // Listen for invite code updates
  useEffect(() => {
    const handleCodeGenerated = () => {
      // WebSocket event doesn't include full creator info, so we refetch to get complete data
      fetchInviteCodes();
    };

    const handleCodeUsed = (data: { id: string; status: string }) => {
      setInviteCodes((prev) =>
        prev.map((code) =>
          String(code.id) === String(data.id) ? { ...code, status: 'used' } : code,
        ),
      );
    };

    const handleCodeRevoked = (data: { id: string }) => {
      setInviteCodes((prev) =>
        prev.map((code) =>
          String(code.id) === String(data.id) ? { ...code, status: 'revoked' } : code,
        ),
      );
    };

    const handleCodeExpired = (data: { id: string }) => {
      setInviteCodes((prev) =>
        prev.map((code) =>
          String(code.id) === String(data.id) ? { ...code, status: 'expired' } : code,
        ),
      );
    };

    const handleAllCodesRevoked = () => {
      setInviteCodes((prev) =>
        prev.map((code) => (code.status === 'active' ? { ...code, status: 'revoked' } : code)),
      );
    };

    socketService.on('invite:code:generated', handleCodeGenerated);
    socketService.on('invite:code:used', handleCodeUsed);
    socketService.on('invite:code:revoked', handleCodeRevoked);
    socketService.on('invite:code:expired', handleCodeExpired);
    socketService.on('invite:all_codes_revoked', handleAllCodesRevoked);

    return () => {
      socketService.off('invite:code:generated', handleCodeGenerated);
      socketService.off('invite:code:used', handleCodeUsed);
      socketService.off('invite:code:revoked', handleCodeRevoked);
      socketService.off('invite:code:expired', handleCodeExpired);
      socketService.off('invite:all_codes_revoked', handleAllCodesRevoked);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      const incoming = (res.data && (res.data.users ?? res.data.data?.users ?? res.data)) || [];
      if (Array.isArray(incoming)) {
        setUsers(incoming as User[]);
      } else {
        console.warn('Unexpected users payload shape:', res.data);
        setUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to fetch users');
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await api.get('/admin/channels');
      setChannels(Array.isArray(res.data?.channels) ? res.data.channels : []);
    } catch (err) {
      console.error('Failed to fetch channels:', err);
      toast.error('Failed to fetch channels');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data?.stats || null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      toast.error('Failed to fetch stats');
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('/admin/roles');
      const rolesList = Array.isArray(res.data?.roles) ? res.data.roles : [];
      setAvailableRoles(rolesList);
      if (rolesList.length > 0) {
        setSelectedRoleId(rolesList[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      toast.error('Failed to fetch roles');
    }
  };

  const fetchChannelMembers = async (channelId: string) => {
    try {
      const res = await api.get(`/admin/channels/${channelId}/members`);
      setChannelMembers(Array.isArray(res.data?.members) ? res.data.members : []);
    } catch (err) {
      console.error('Failed to fetch channel members:', err);
      toast.error('Failed to fetch channel members');
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await api.get('/api/announcements');
      setAnnouncements(Array.isArray(res.data?.announcements) ? res.data.announcements : []);
    } catch (err: any) {
      console.error('Failed to fetch announcements:', err);
      toast.error(err.response?.data?.error || 'Failed to fetch announcements');
    }
  };

  const openMemberModal = async (channel: Channel) => {
    setSelectedChannel(channel);
    setShowMemberModal(true);
    await fetchChannelMembers(channel.id);
  };

  const closeMemberModal = () => {
    setShowMemberModal(false);
    setSelectedChannel(null);
    setChannelMembers([]);
    setSelectedUserId('');
  };

  const addChannelMember = async () => {
    if (!selectedChannel || !selectedUserId || !selectedRoleId) {
      toast.error('Please select both a user and a role');
      return;
    }
    try {
      await api.post(`/admin/channels/${selectedChannel.id}/members`, {
        userId: selectedUserId,
        roleId: selectedRoleId,
      });
      toast.success('Member added to channel');
      fetchChannelMembers(selectedChannel.id);
      setSelectedUserId('');
    } catch (err: any) {
      console.error('Failed to add member:', err);
      toast.error(err.response?.data?.error || 'Failed to add member');
    }
  };

  const updateMemberRole = async (userId: string, newRoleId: string) => {
    if (!selectedChannel) return;
    try {
      await api.put(`/admin/channels/${selectedChannel.id}/members/${userId}`, {
        roleId: newRoleId,
      });
      toast.success('Member role updated');
      fetchChannelMembers(selectedChannel.id);
    } catch (err: any) {
      console.error('Failed to update member role:', err);
      toast.error(err.response?.data?.error || 'Failed to update member role');
    }
  };

  const removeChannelMember = async (userId: string, username: string) => {
    if (!selectedChannel) return;
    if (!confirm(`Remove ${username} from ${selectedChannel.name}?`)) {
      return;
    }
    try {
      await api.delete(`/admin/channels/${selectedChannel.id}/members/${userId}`);
      toast.success('Member removed from channel');
      fetchChannelMembers(selectedChannel.id);
    } catch (err: any) {
      console.error('Failed to remove member:', err);
      toast.error(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const toggleUserAdmin = async (userId: string, currentStatus: boolean) => {
    try {
      await api.put(`/admin/users/${userId}/admin`, { isAdmin: !currentStatus });
      toast.success('User admin status updated');
      fetchUsers();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      toast.error(err.response?.data?.error || 'Failed to update user');
    }
  };

  const deleteUser = async (userId: string, username: string) => {
    if (
      !confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)
    ) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      toast.error(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const generateUserResetLink = async (userId: string, username: string) => {
    try {
      await api.post(`/admin/users/${userId}/reset-link`);
      toast.success(`Password reset link sent to ${username}'s email`);
    } catch (err: any) {
      console.error('Failed to generate reset link:', err);
      toast.error(err.response?.data?.error || 'Failed to generate reset link');
    }
  };

  const revokeUserSessions = async (userId: string, username: string) => {
    if (!confirm(`Force log out all active sessions for "${username}"?`)) {
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/revoke-sessions`);
      toast.success(`Revoked all active sessions for ${username}`);
    } catch (err: any) {
      console.error('Failed to revoke user sessions:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke user sessions');
    }
  };

  const [revokingAllTokens, setRevokingAllTokens] = useState(false);

  const revokeAllPlatformTokens = async () => {
    if (
      !confirm(
        '⚠️ CRITICAL ACTION: Are you sure you want to invalidate ALL platform tokens?\n\nEvery user across all browsers, mobile devices, and sessions will be immediately logged out and required to sign in again.',
      )
    ) {
      return;
    }
    setRevokingAllTokens(true);
    try {
      const res = await api.post('/admin/revoke-all-platform-tokens');
      if (res.data?.accessToken) {
        localStorage.setItem('accessToken', res.data.accessToken);
      }
      if (res.data?.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }
      toast.success('All platform tokens have been invalidated. All users logged out.');
      fetchUsers();
    } catch (err: any) {
      console.error('Failed to revoke platform tokens:', err);
      toast.error(err.response?.data?.error || 'Failed to invalidate platform tokens');
    } finally {
      setRevokingAllTokens(false);
    }
  };

  const deleteChannel = async (channelId: string, channelName: string) => {
    if (
      !confirm(
        `Are you sure you want to delete channel "${channelName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/channels/${channelId}`);
      toast.success('Channel deleted');
      fetchChannels();
    } catch (err: any) {
      console.error('Failed to delete channel:', err);
      toast.error(err.response?.data?.error || 'Failed to delete channel');
    }
  };

  const startEditChannel = (channel: Channel) => {
    setEditingChannel(channel);
    setEditChannelName(channel.name);
    setEditChannelDescription(channel.description || '');
    setEditChannelPrivate(channel.isPrivate);
    setEditChannelVoice(channel.isVoice);
    setEditChannelRetention(channel.retentionDays ? String(channel.retentionDays) : '');
  };

  const cancelEditChannel = () => {
    setEditingChannel(null);
    setEditChannelName('');
    setEditChannelDescription('');
    setEditChannelPrivate(false);
    setEditChannelVoice(false);
    setEditChannelRetention('');
    setSavingChannel(false);
  };

  const updateChannel = async () => {
    if (!editingChannel) return;
    if (!editChannelName.trim()) {
      toast.error('Channel name is required');
      return;
    }

    try {
      setSavingChannel(true);
      await api.put(`/admin/channels/${editingChannel.id}`, {
        name: editChannelName.trim(),
        description: editChannelDescription.trim() || null,
        isPrivate: editChannelPrivate,
        isVoice: editChannelVoice,
        retentionDays: editChannelRetention ? parseInt(editChannelRetention) : null,
      });
      toast.success('Channel updated');
      cancelEditChannel();
      fetchChannels();
    } catch (err: any) {
      console.error('Failed to update channel:', err);
      toast.error(err.response?.data?.error || 'Failed to update channel');
    } finally {
      setSavingChannel(false);
    }
  };

  const createChannel = async () => {
    if (!newChannelName.trim()) {
      toast.error('Channel name is required');
      return;
    }

    try {
      setCreatingChannel(true);
      const res = await api.post('/admin/channels', {
        name: newChannelName.trim(),
        description: newChannelDescription.trim() || null,
        isPrivate: newChannelPrivate,
        isVoice: newChannelVoice,
        retentionDays: newChannelRetention ? parseInt(newChannelRetention) : null,
      });

      const createdChannelId = res.data?.channel?.id;
      const defaultRoleId = availableRoles[0]?.id;

      if (createdChannelId && newChannelPrivate && newChannelMembers.length > 0) {
        if (!defaultRoleId) {
          toast.error('No roles available to assign to new members');
        } else {
          await Promise.all(
            newChannelMembers.map((userId) =>
              api.post(`/admin/channels/${createdChannelId}/members`, {
                userId,
                roleId: defaultRoleId,
              }),
            ),
          );
        }
      }

      toast.success('Channel created');
      setNewChannelName('');
      setNewChannelDescription('');
      setNewChannelPrivate(false);
      setNewChannelVoice(false);
      setNewChannelRetention('');
      setNewChannelMembers([]);
      fetchChannels();
    } catch (err: any) {
      console.error('Failed to create channel:', err);
      toast.error(err.response?.data?.error || 'Failed to create channel');
    } finally {
      setCreatingChannel(false);
    }
  };

  const persistChannelOrder = async (orderedChannels: Channel[]) => {
    setChannels(orderedChannels);
    try {
      setSavingOrder(true);
      const res = await api.put('/admin/channels/reorder', {
        channelIds: orderedChannels.map((c) => c.id),
      });
      setChannels(res.data.channels);
      toast.success('Channel order updated');
    } catch (err: any) {
      console.error('Failed to reorder channels:', err);
      toast.error(err.response?.data?.error || 'Failed to reorder channels');
    } finally {
      setSavingOrder(false);
    }
  };

  const moveChannel = (channelId: string, direction: -1 | 1) => {
    const index = channels.findIndex((c) => String(c.id) === String(channelId));
    const targetIndex = index + direction;
    if (index === -1 || targetIndex < 0 || targetIndex >= channels.length) return;

    const updated = [...channels];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, moved);
    persistChannelOrder(updated);
  };

  const sendAnnouncement = async () => {
    if (!announcement) {
      toast.error('Announcement message cannot be empty');
      return;
    }

    try {
      const payload: any = {
        title: 'Announcement',
        message: announcement,
        type: announcementType,
      };

      if (enableExpiry) {
        if (expiryMode === 'absolute' && announcementExpiry) {
          const now = new Date();
          const expiry = new Date(announcementExpiry);
          const diffMs = expiry.getTime() - now.getTime();
          const diffHours = diffMs / (1000 * 60 * 60);
          if (diffHours <= 0) {
            toast.error('Expiration must be in the future.');
            return;
          }
          payload.expiresInHours = Math.ceil(diffHours);
        } else if (expiryMode === 'duration') {
          const days = parseInt(expiryDurationDays) || 0;
          const hours = parseInt(expiryDurationHours) || 0;
          const minutes = parseInt(expiryDurationMinutes) || 0;
          if (days === 0 && hours === 0 && minutes === 0) {
            toast.error('Please specify a duration for expiration.');
            return;
          }
          const totalHours = days * 24 + hours + minutes / 60;
          payload.expiresInHours = Math.ceil(totalHours);
        }
      }

      const response = await api.post('/admin/announcement', payload);
      toast.success('Announcement sent successfully');

      // Update local state immediately
      if (response.data.announcement) {
        setAnnouncements((prev) => [response.data.announcement, ...prev]);
      }

      setAnnouncement('');
      setAnnouncementExpiry('');
      setExpiryDurationDays('7');
      setExpiryDurationHours('0');
      setExpiryDurationMinutes('0');
      setEnableExpiry(false);
      setExpiryMode('duration');
    } catch (err: any) {
      console.error('Failed to send announcement:', err);
      toast.error(err.response?.data?.error || 'Failed to send announcement');
    }
  };

  const revokeAnnouncement = async (announcementId: string) => {
    if (!confirm('Are you sure you want to revoke this announcement?')) {
      return;
    }

    try {
      await api.delete(`/admin/announcement/${announcementId}`);
      toast.success('Announcement revoked successfully');

      // Update local state immediately
      setAnnouncements((prev) => prev.filter((a) => String(a.id) !== String(announcementId)));
    } catch (err: any) {
      console.error('Failed to revoke announcement:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke announcement');
    }
  };

  const fetchInviteCodes = async () => {
    try {
      const res = await api.get('/api/invites/list');
      setInviteCodes(res.data.codes || []);
    } catch (err: any) {
      console.error('Failed to fetch invite codes:', err);
      toast.error(err.response?.data?.error || 'Failed to fetch invite codes');
    }
  };

  const generateInviteCode = async () => {
    try {
      await api.post('/api/invites/generate');
      toast.success('Invite code generated successfully');
      // Refetch to get the complete code with creator info
      await fetchInviteCodes();
    } catch (err: any) {
      console.error('Failed to generate invite code:', err);
      toast.error(err.response?.data?.error || 'Failed to generate invite code');
    }
  };

  const revokeCode = async (codeId: string) => {
    if (!confirm('Are you sure you want to revoke this invite code?')) {
      return;
    }

    try {
      await api.post(`/api/invites/revoke/${codeId}`);
      toast.success('Invite code revoked successfully');
      setInviteCodes((prev) =>
        prev.map((code) =>
          String(code.id) === String(codeId) ? { ...code, status: 'revoked' } : code,
        ),
      );
    } catch (err: any) {
      console.error('Failed to revoke invite code:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke invite code');
    }
  };

  const revokeAllCodes = async () => {
    if (
      !confirm(
        'Are you sure you want to revoke ALL active invite codes? This action cannot be undone.',
      )
    ) {
      return;
    }

    try {
      await api.post('/api/invites/revoke-all');
      toast.success('All invite codes revoked successfully');
      setInviteCodes((prev) =>
        prev.map((code) => (code.status === 'active' ? { ...code, status: 'revoked' } : code)),
      );
    } catch (err: any) {
      console.error('Failed to revoke all invite codes:', err);
      toast.error(err.response?.data?.error || 'Failed to revoke all invite codes');
    }
  };

  const generateInvite = async () => {
    try {
      const res = await api.post('/invites/generate', {
        expiresInHours: inviteExpiry,
      });
      // Compose a link if you want, but always show the code
      const code = res.data.code;
      setGeneratedInviteCode(code);
      // Optionally, you can build a registration link if your frontend supports it
      const inviteUrl = `${window.location.origin}/register?invite=${code}`;
      setGeneratedInvite(inviteUrl);
      toast.success('Invite code generated');
    } catch (err: any) {
      console.error('Failed to generate invite:', err);
      toast.error(err.response?.data?.error || 'Failed to generate invite');
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(generatedInvite);
    toast.success('Invite link copied to clipboard');
  };

  const fetchServicesOverview = async () => {
    setLoadingServices(true);
    try {
      const [servicesRes, settingsRes] = await Promise.all([
        api.get('/admin/services-overview'),
        api.get('/admin/settings'),
      ]);
      setServicesOverview(servicesRes.data.services);
      if (settingsRes.data?.settings) {
        setServerSettings(settingsRes.data.settings);
      }
    } catch (e: any) {
      toast.error('Failed to load server services and settings');
    } finally {
      setLoadingServices(false);
    }
  };

  const handleSaveServerSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put('/admin/settings', serverSettings);
      toast.success('Server settings saved successfully!');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to save server settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await api.post('/admin/services/test-smtp');
      toast.success(res.data.message || 'Test email sent!');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to send test email');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestStorage = async () => {
    setTestingStorage(true);
    try {
      const res = await api.post('/admin/services/test-storage');
      toast.success(res.data.message || 'Storage probe verified!');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Storage probe failed');
    } finally {
      setTestingStorage(false);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const res = await api.post('/admin/services/test-push');
      toast.success(res.data.message || 'Test push notification sent!');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to send test push');
    } finally {
      setTestingPush(false);
    }
  };

  const handleExportServerData = async () => {
    try {
      const res = await api.get('/admin/export-data');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `irus_server_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Server migration bundle downloaded!');
    } catch (e: any) {
      toast.error('Failed to export server data');
    }
  };

  const handleImportServerData = async (file: File) => {
    if (
      !window.confirm(
        'Importing server data will restore users, settings, and channels. Are you sure you want to proceed?',
      )
    ) {
      return;
    }
    setImportingData(true);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const res = await api.post('/admin/import-data', bundle);
      toast.success(res.data.message || 'Server data imported successfully!');
      fetchUsers();
      fetchChannels();
      fetchServicesOverview();
    } catch (e: any) {
      toast.error(
        e.response?.data?.error ||
          'Failed to import server migration bundle. Please verify JSON format.',
      );
    } finally {
      setImportingData(false);
    }
  };

  const handleDownloadSqlDump = async () => {
    setDownloadingSql(true);
    try {
      const res = await api.get('/admin/database-dump', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/sql' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `irus_full_database_${new Date().toISOString().slice(0, 10)}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Full database SQL dump downloaded successfully!');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to download database dump');
    } finally {
      setDownloadingSql(false);
    }
  };

  const handleRestoreSqlDump = async (file: File) => {
    if (
      !window.confirm(
        '⚠️ CRITICAL WARNING: Restoring a raw PostgreSQL database dump will overwrite existing tables, messages, users, and channels. Are you sure you want to proceed with full database migration?',
      )
    ) {
      return;
    }
    setRestoringSql(true);
    try {
      const formData = new FormData();
      formData.append('databaseFile', file);
      const res = await api.post('/admin/database-restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message || 'Full database restored successfully!');
      fetchUsers();
      fetchChannels();
      fetchServicesOverview();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to restore database from SQL file.');
    } finally {
      setRestoringSql(false);
    }
  };

  // Define which permissions are needed for each tab
  const tabPermissions: Record<string, PermissionName[]> = {
    stats: [PermissionName.EDIT_SERVER_SETTINGS],
    users: [PermissionName.MANAGE_USERS],
    invites: [PermissionName.EDIT_SERVER_SETTINGS],
    channels: [PermissionName.MANAGE_CHANNEL_SETTINGS, PermissionName.CREATE_CHANNELS],
    services: [PermissionName.EDIT_SERVER_SETTINGS],
    migration: [PermissionName.EDIT_SERVER_SETTINGS],
    retention: [PermissionName.MANAGE_RETENTION_POLICY],
    announcements: [PermissionName.EDIT_SERVER_SETTINGS],
  };

  // Filter tabs based on permissions
  const availableTabs = (
    [
      'stats',
      'users',
      'invites',
      'channels',
      'services',
      'migration',
      'retention',
      'announcements',
    ] as const
  ).filter((tab) => {
    const requiredPerms = tabPermissions[tab] || [];
    return requiredPerms.some((perm) => hasPermission(perm));
  });

  // Show loading state while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-brand-background dark:bg-brand-background">
        <div className="text-brand-on-surface/60">Loading...</div>
      </div>
    );
  }

  // Don't render if no admin permissions
  if (!hasAnyAdminPermission) {
    return null;
  }

  const workspaceContent = (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* Header with mobile menu button */}
      <div className="bg-brand-surface/95 dark:bg-[#19163e]/95 backdrop-blur-xl border-b border-brand-outline-variant/60 px-4 sm:px-6 py-3.5 flex items-center justify-between shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {isMobile && (
            <button
              onClick={onOpenSidebar || (() => setSidebarOpen(!sidebarOpen))}
              className="p-2.5 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer"
              aria-label="Open sidebar menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-brand-on-background dark:text-[#e0e7ff] tracking-tight truncate flex items-center gap-2">
              <span>🛡️</span> Admin Panel
            </h1>
            <p className="text-xs text-brand-outline dark:text-[#c4b5fd] truncate hidden sm:block">
              Manage server configuration, users, roles, and channels
            </p>
          </div>
        </div>

        <button
          onClick={onExit || (() => navigate('/chat'))}
          className="btn btn-outlined text-xs sm:text-sm px-4 py-1.5 rounded-full font-semibold border border-brand-outline-variant text-brand-on-surface hover:bg-white/10 hover:text-white active:scale-95 transition-all cursor-pointer flex items-center gap-1.5 flex-shrink-0"
        >
          <span>✕</span> Exit to Chat
        </button>
      </div>

      {/* Tabs - Material Design 3 Segmented Pill Bar */}
      <div className="px-6 py-3 border-b border-brand-outline-variant/60 bg-brand-surface/50 dark:bg-black/20 backdrop-blur-md">
        <div
          className="flex gap-2 overflow-x-auto p-1 bg-brand-surface-variant/40 dark:bg-[#18153b]/80 rounded-2xl border border-brand-outline-variant/40 w-fit max-w-full"
          ref={tabListRef}
          role="tablist"
          onKeyDown={handleTabsKeyDown}
        >
          {availableTabs.map((tab) => (
            <button
              key={tab}
              data-tab={tab}
              role="tab"
              aria-selected={activeTab === tab}
              tabIndex={activeTab === tab ? 0 : -1}
              onClick={() => setActiveTab(tab)}
              onKeyDown={handleTabsKeyDown}
              className={`px-5 py-2 font-semibold text-xs sm:text-sm capitalize transition-all duration-200 rounded-xl whitespace-nowrap flex-shrink-0 touch-manipulation cursor-pointer active:scale-95 ${
                activeTab === tab
                  ? 'bg-brand-primary text-brand-on-primary font-bold shadow-md shadow-amber-500/25 scale-[1.02]'
                  : 'text-brand-on-surface-variant dark:text-[#c4b5fd] hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center gap-2">
                {tab === 'stats' && '📊'}
                {tab === 'users' && '👥'}
                {tab === 'invites' && '🎫'}
                {tab === 'channels' && '📡'}
                {tab === 'services' && '⚙️'}
                {tab === 'migration' && '📦'}
                {tab === 'retention' && '🗃️'}
                {tab === 'announcements' && '📢'}
                {tab === 'services'
                  ? 'Services & Settings'
                  : tab === 'migration'
                    ? 'Backup & Migration'
                    : tab}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 relative">
        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats && (
              <>
                {/* Total Users */}
                <div className="group relative bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden">
                  <div className="absolute top-4 right-4 text-4xl opacity-15">
                    👥
                  </div>
                  <div className="relative z-10">
                    <div className="text-xs font-semibold text-brand-outline dark:text-[#c4b5fd] uppercase tracking-wider mb-1 flex items-center gap-2">
                      Total Registered Users
                    </div>
                    <div className="text-4xl font-extrabold text-amber-400 mt-2">
                      {stats.totalUsers}
                    </div>
                  </div>
                </div>

                {/* Online Users */}
                <div className="group relative bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden">
                  <div className="absolute top-4 right-4 text-4xl opacity-15">
                    💚
                  </div>
                  <div className="relative z-10">
                    <div className="text-xs font-semibold text-brand-outline dark:text-[#c4b5fd] uppercase tracking-wider mb-1 flex items-center gap-2">
                      Active Online Users
                    </div>
                    <div className="text-4xl font-extrabold text-emerald-400 mt-2">
                      {stats.onlineUsers}
                    </div>
                  </div>
                </div>

                {/* Total Channels */}
                <div className="group relative bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden">
                  <div className="absolute top-4 right-4 text-4xl opacity-15">
                    📡
                  </div>
                  <div className="relative z-10">
                    <div className="text-xs font-semibold text-brand-outline dark:text-[#c4b5fd] uppercase tracking-wider mb-1 flex items-center gap-2">
                      Total Channels
                    </div>
                    <div className="text-4xl font-extrabold text-cyan-400 mt-2">
                      {stats.totalChannels}
                    </div>
                  </div>
                </div>

                {/* Total Messages */}
                <div className="group relative bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden">
                  <div className="absolute top-4 right-4 text-4xl opacity-15">
                    📜
                  </div>
                  <div className="relative z-10">
                    <div className="text-xs font-semibold text-brand-outline dark:text-[#c4b5fd] uppercase tracking-wider mb-1 flex items-center gap-2">
                      Total Messages
                    </div>
                    <div className="text-4xl font-extrabold text-purple-400 mt-2">
                      {stats.totalMessages.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* New Users */}
                <div className="group relative bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden">
                  <div className="absolute top-4 right-4 text-4xl opacity-15">
                    ✨
                  </div>
                  <div className="relative z-10">
                    <div className="text-xs font-semibold text-brand-outline dark:text-[#c4b5fd] uppercase tracking-wider mb-1 flex items-center gap-2">
                      New Users (Last 7 Days)
                    </div>
                    <div className="text-4xl font-extrabold text-rose-400 mt-2">
                      {stats.recentUsers}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="relative rounded-3xl border border-brand-outline-variant/60 shadow-xl bg-brand-surface/80 dark:bg-[#252350]/80 overflow-hidden">
            {/* New User Dropdown */}
            <div className="flex justify-end px-6 py-4">
              <div className="relative inline-block text-left">
                <button
                  type="button"
                  className="btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed shadow-md text-xs sm:text-sm px-5 py-2.5 rounded-full"
                  disabled={!canGenerateInvites}
                  id="new-user-dropdown-btn"
                  onClick={() => canGenerateInvites && setShowInviteDropdown((v) => !v)}
                >
                  + New User Invite ▼
                </button>
                {showInviteDropdown && (
                  <DropdownPortal anchorId="new-user-dropdown-btn" width={320}>
                    <div
                      className="rounded-3xl shadow-2xl bg-brand-surface/95 dark:bg-[#1f1c48]/95 backdrop-blur-xl border border-brand-outline-variant/60 z-50 p-5 animate-m3-dialog"
                      style={{ maxHeight: '60vh', overflowY: 'auto' }}
                    >
                      <h2 className="text-lg font-semibold text-brand-on-background dark:text-brand-on-primary mb-4">
                        Generate Invite Link
                      </h2>
                      <div className="space-y-4">
                        {!canGenerateInvites && (
                          <div className="text-sm text-brand-primary bg-brand-primary/10 border border-brand-primary/30 rounded-md p-3">
                            You need EDIT_SERVER_SETTINGS permission to generate invites. Please
                            contact an admin.
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                            Expiration Time (hours)
                          </label>
                          <input
                            type="number"
                            value={inviteExpiry}
                            onChange={(e) => setInviteExpiry(parseInt(e.target.value) || 24)}
                            min="1"
                            max="168"
                            className="input w-full"
                            disabled={!canGenerateInvites}
                          />
                          <div className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                            Max: 168 hours (7 days)
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!canGenerateInvites) return;
                            await generateInvite();
                            setShowInviteDropdown(true);
                          }}
                          className="btn btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                          disabled={!canGenerateInvites}
                        >
                          Generate Invite Link
                        </button>
                        {(generatedInviteCode || generatedInvite) && (
                          <div className="bg-brand-background dark:bg-brand-surface-variant rounded-lg p-4 space-y-2">
                            {generatedInviteCode && (
                              <div>
                                <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                                  Invite Code
                                </label>
                                <div className="flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={generatedInviteCode}
                                    readOnly
                                    className="input flex-1 bg-brand-surface dark:bg-brand-surface-variant font-mono text-lg text-center"
                                  />
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(generatedInviteCode);
                                      toast.success('Invite code copied!');
                                    }}
                                    className="btn btn-secondary whitespace-nowrap"
                                  >
                                    Copy Code
                                  </button>
                                </div>
                              </div>
                            )}
                            {generatedInvite && (
                              <div>
                                <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                                  Registration Link
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={generatedInvite}
                                    readOnly
                                    className="input flex-1 bg-brand-surface dark:bg-brand-surface-variant"
                                  />
                                  <button
                                    onClick={copyInviteLink}
                                    className="btn btn-secondary whitespace-nowrap"
                                  >
                                    Copy Link
                                  </button>
                                </div>
                                <div className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-2">
                                  Share this link or code with users you want to invite. They can
                                  use it to create an account.
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </DropdownPortal>
                )}
              </div>
            </div>

            {/* Global Session Security Action Card */}
            <div className="bg-red-500/10 dark:bg-red-950/30 border border-red-500/30 dark:border-red-800/50 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
              <div>
                <h4 className="text-sm font-bold text-red-500 dark:text-red-400 flex items-center gap-2">
                  <span>🚨</span> Server Security: Invalidate All Platform Tokens
                </h4>
                <p className="text-xs text-brand-on-surface-variant dark:text-[#c4b5fd] mt-1">
                  Immediately forces a global session reset across all devices, mobile clients, and browsers for every user.
                </p>
              </div>
              <button
                onClick={revokeAllPlatformTokens}
                disabled={revokingAllTokens}
                className="btn bg-brand-error hover:bg-red-700 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl cursor-pointer flex-shrink-0 transition-all active:scale-95 shadow-md disabled:opacity-50"
              >
                {revokingAllTokens ? 'Invalidating…' : '🚨 Invalidate All Tokens (Force Logout Everyone)'}
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-surface border-b-2 border-brand-primary/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-brand-on-background dark:text-brand-on-primary uppercase tracking-wider">
                      <span className="flex items-center gap-2">👤 Username</span>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-brand-on-background dark:text-brand-on-primary uppercase tracking-wider">
                      <span className="flex items-center gap-2">✉️ Email</span>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-brand-on-background dark:text-brand-on-primary uppercase tracking-wider">
                      <span className="flex items-center gap-2">🌟 Status</span>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-brand-on-background dark:text-brand-on-primary uppercase tracking-wider">
                      <span className="flex items-center gap-2">👑 Role</span>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-brand-on-background dark:text-brand-on-primary uppercase tracking-wider">
                      <span className="flex items-center gap-2">📅 Created</span>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-brand-on-background dark:text-brand-on-primary uppercase tracking-wider">
                      <span className="flex items-center gap-2">⚡ Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-500/10">
                  {users.map((user, index) => (
                    <tr
                      key={user.id}
                      className="group hover:bg-brand-secondary/10 transition-colors duration-200"
                      style={{
                        animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-brand-on-background dark:text-brand-on-background group-hover:text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                        {user.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-on-surface/70">
                        {user.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border ${
                            user.status === 'online'
                              ? 'bg-brand-tertiary/20 text-brand-tertiary border-brand-tertiary/30 shadow-brand-primary/20'
                              : user.status === 'idle'
                                ? 'bg-brand-primary/20 text-brand-on-background dark:text-brand-on-primary border-brand-primary/30 shadow-brand-primary/20'
                                : user.status === 'dnd'
                                  ? 'bg-brand-secondary/20 text-brand-secondary border-brand-secondary/30 shadow-brand-primary/20'
                                  : 'bg-brand-surface/20 text-brand-on-surface border-brand-outline/30 shadow-brand-surface/20'
                          } shadow-lg`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${
                            user.isOwner
                              ? 'bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-brand-on-surface-variant dark:text-brand-on-surface-variant border border-brand-primary/40'
                              : user.isAdmin
                                ? 'bg-gradient-to-r from-brand-secondary/30 to-brand-error/30 text-brand-on-surface border border-brand-secondary/40'
                                : 'bg-brand-surface/20 text-brand-on-surface border border-brand-outline/30'
                          }`}
                        >
                          {user.isOwner && '👑 '}
                          {user.isAdmin && !user.isOwner && '⚡ '}
                          {user.isOwner
                            ? 'Divine Owner'
                            : user.isAdmin
                              ? 'Administrator'
                              : 'Mortal'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-on-surface/70">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                        {!user.isOwner && (
                          <>
                            <button
                              onClick={() => toggleUserAdmin(user.id, user.isAdmin)}
                              className="text-brand-tertiary hover:text-brand-tertiary font-medium transition-all duration-200 hover:scale-110 inline-block"
                            >
                              {user.isAdmin ? '↓ Demote' : '↑ Promote'}
                            </button>
                            <button
                              onClick={() => generateUserResetLink(user.id, user.username)}
                              className="text-brand-tertiary hover:text-brand-tertiary font-medium transition-all duration-200 hover:scale-110 inline-block"
                            >
                              🔑 Reset
                            </button>
                            <button
                              onClick={() => revokeUserSessions(user.id, user.username)}
                              className="text-amber-500 hover:text-amber-400 font-medium transition-all duration-200 hover:scale-110 inline-block"
                              title="Force log out all active sessions for this user"
                            >
                              🔒 Revoke
                            </button>
                            <button
                              onClick={() => deleteUser(user.id, user.username)}
                              className="text-brand-secondary hover:text-brand-secondary font-medium transition-all duration-200 hover:scale-110 inline-block"
                            >
                              ⚔️ Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'channels' && (
          <div className="space-y-6">
            <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl border border-brand-outline-variant/60 shadow-xl p-6">
              <h3 className="text-lg font-semibold text-brand-on-background dark:text-[#e0e7ff] mb-4">
                Create Channel
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                    Name
                  </label>
                  <input
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)}
                    className="input w-full"
                    placeholder="general"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                    Description
                  </label>
                  <input
                    value={newChannelDescription}
                    onChange={(e) => setNewChannelDescription(e.target.value)}
                    className="input w-full"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                    Retention (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newChannelRetention}
                    onChange={(e) => setNewChannelRetention(e.target.value)}
                    className="input w-full"
                    placeholder="Leave empty for no limit"
                  />
                  <p className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                    Overrides global retention for this channel.
                  </p>
                </div>
                <label className="inline-flex items-center space-x-2 text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                  <input
                    type="checkbox"
                    checked={newChannelPrivate}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setNewChannelPrivate(next);
                      if (!next) {
                        setNewChannelMembers([]);
                      }
                    }}
                    className="form-checkbox h-4 w-4 text-primary-600"
                  />
                  <span>Private</span>
                </label>
                <label className="inline-flex items-center space-x-2 text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                  <input
                    type="checkbox"
                    checked={newChannelVoice}
                    onChange={(e) => setNewChannelVoice(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-primary-600"
                  />
                  <span>Voice channel</span>
                </label>

                {newChannelPrivate && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                      Add members now (optional)
                    </label>
                    <p className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mb-2">
                      Selected users will be added immediately with role:{' '}
                      {availableRoles[0]?.name ?? 'first available role'}.
                    </p>
                    {users.length === 0 ? (
                      <div className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                        No users available.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto rounded border border-brand-outline-variant dark:border-brand-outline-variant p-2">
                        {users.map((user) => {
                          const checked = newChannelMembers.includes(user.id);
                          return (
                            <label
                              key={user.id}
                              className="flex items-center gap-2 text-sm text-brand-on-surface dark:text-brand-on-surface"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setNewChannelMembers((prev) =>
                                    prev.includes(user.id)
                                      ? prev.filter((id) => id !== user.id)
                                      : [...prev, user.id],
                                  )
                                }
                                className="form-checkbox h-4 w-4 text-primary-600"
                              />
                              <span>{user.username}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={createChannel}
                  disabled={creatingChannel}
                  className="btn btn-primary"
                >
                  {creatingChannel ? 'Creating...' : 'Create Channel'}
                </button>
              </div>
            </div>

            {editingChannel && (
              <div className="bg-brand-surface dark:bg-brand-surface rounded-lg shadow p-6 border border-primary-100 dark:border-primary-900/40">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-brand-on-background dark:text-brand-on-primary">
                    Edit Channel: {editingChannel.name}
                  </h3>
                  <button
                    onClick={cancelEditChannel}
                    className="text-sm text-brand-on-surface/50 hover:text-brand-on-surface dark:text-brand-on-surface-variant dark:hover:text-brand-on-surface"
                  >
                    Cancel
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                      Name
                    </label>
                    <input
                      value={editChannelName}
                      onChange={(e) => setEditChannelName(e.target.value)}
                      className="input w-full"
                      placeholder="channel-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                      Description
                    </label>
                    <input
                      value={editChannelDescription}
                      onChange={(e) => setEditChannelDescription(e.target.value)}
                      className="input w-full"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                      Retention (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={editChannelRetention}
                      onChange={(e) => setEditChannelRetention(e.target.value)}
                      className="input w-full"
                      placeholder="Leave empty for no limit"
                    />
                    <p className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                      Leave empty to fall back to global policy.
                    </p>
                  </div>
                  <label className="inline-flex items-center space-x-2 text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                    <input
                      type="checkbox"
                      checked={editChannelPrivate}
                      onChange={(e) => setEditChannelPrivate(e.target.checked)}
                      className="form-checkbox h-4 w-4 text-primary-600"
                    />
                    <span>Private</span>
                  </label>
                  <label className="inline-flex items-center space-x-2 text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                    <input
                      type="checkbox"
                      checked={editChannelVoice}
                      onChange={(e) => setEditChannelVoice(e.target.checked)}
                      className="form-checkbox h-4 w-4 text-primary-600"
                    />
                    <span>Voice channel</span>
                  </label>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={cancelEditChannel} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button
                    onClick={updateChannel}
                    disabled={savingChannel}
                    className="btn btn-primary"
                  >
                    {savingChannel ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            <div className="bg-brand-surface dark:bg-brand-surface rounded-lg shadow overflow-hidden">
              <div className="px-6 py-3 text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                Drag via arrows to reorder (saves immediately).
              </div>
              <table className="w-full">
                <thead className="bg-brand-background dark:bg-brand-surface-variant">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Retention
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {channels.map((channel, index) => (
                    <tr key={channel.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => moveChannel(channel.id, -1)}
                            disabled={index === 0 || savingOrder}
                            className="px-2 py-1 text-xs rounded bg-brand-background dark:bg-brand-surface-variant text-brand-on-surface dark:text-brand-on-surface disabled:opacity-50"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveChannel(channel.id, 1)}
                            disabled={index === channels.length - 1 || savingOrder}
                            className="px-2 py-1 text-xs rounded bg-brand-background dark:bg-brand-surface-variant text-brand-on-surface dark:text-brand-on-surface disabled:opacity-50"
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-brand-on-background dark:text-brand-on-primary">
                        {channel.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                        {channel.description || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                        {channel.retentionDays ? `${channel.retentionDays}d` : 'Global / Unlimited'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap space-x-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            channel.isVoice
                              ? 'bg-brand-secondary text-brand-on-secondary'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}
                        >
                          {channel.isVoice ? 'Voice' : 'Text'}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            channel.isPrivate
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}
                        >
                          {channel.isPrivate ? 'Private' : 'Public'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                        {new Date(channel.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                        <button
                          onClick={() => startEditChannel(channel)}
                          className="text-brand-primary hover:text-brand-primary-dark dark:text-brand-primary dark:hover:text-brand-primary-container"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openMemberModal(channel)}
                          className="text-brand-primary hover:text-blue-800 dark:text-brand-primary dark:hover:text-blue-300"
                        >
                          Manage Members
                        </button>
                        <button
                          onClick={() => deleteChannel(channel.id, channel.name)}
                          className="text-brand-error hover:text-red-800 dark:text-brand-error dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'retention' && (
          <div className="max-w-5xl">
            <RetentionPolicySettings />
          </div>
        )}

        {/* Invites tab removed. Invite UI will be moved to Users tab. */}

        {activeTab === 'announcements' && (
          <div className="max-w-2xl">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-brand-on-background dark:text-brand-on-primary mb-4">
                Broadcast Announcement
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                    Type
                  </label>
                  <select
                    value={announcementType}
                    onChange={(e) => setAnnouncementType(e.target.value as any)}
                    className="input w-full"
                  >
                    <option value="info">Info</option>
                    <option value="alert">Alert</option>
                    <option value="announcement">Announcement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                    Message
                  </label>
                  <textarea
                    value={announcement}
                    onChange={(e) => setAnnouncement(e.target.value)}
                    placeholder="Enter announcement message..."
                    className="input w-full h-32 resize-none"
                    maxLength={500}
                  />
                  <div className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                    {announcement.length}/500 characters
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-2">
                    Expiration
                  </label>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={enableExpiry}
                        onChange={(e) => {
                          setEnableExpiry(e.target.checked);
                          if (e.target.checked) {
                            setExpiryMode('duration');
                            setExpiryDurationDays('7');
                            setExpiryDurationHours('0');
                            setExpiryDurationMinutes('0');
                          }
                        }}
                        className="form-checkbox"
                      />
                      <span>Enable Expiration</span>
                    </label>
                  </div>
                  {enableExpiry && (
                    <>
                      <div className="flex gap-4 items-center mb-2">
                        <label className="flex items-center gap-1">
                          <input
                            type="radio"
                            checked={expiryMode === 'absolute'}
                            onChange={() => setExpiryMode('absolute')}
                            className="form-radio"
                          />
                          <span>Date/Time</span>
                        </label>
                        <label className="flex items-center gap-1">
                          <input
                            type="radio"
                            checked={expiryMode === 'duration'}
                            onChange={() => setExpiryMode('duration')}
                            className="form-radio"
                          />
                          <span>Duration</span>
                        </label>
                      </div>
                      {expiryMode === 'absolute' ? (
                        <>
                          <input
                            type="datetime-local"
                            value={announcementExpiry}
                            onChange={(e) => setAnnouncementExpiry(e.target.value)}
                            className="input w-full"
                          />
                          <div className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                            Announcement will expire at the selected date and time.
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex gap-2 items-end">
                            <div className="flex flex-col">
                              <label className="text-xs text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                                Days
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={expiryDurationDays}
                                onChange={(e) => setExpiryDurationDays(e.target.value)}
                                placeholder="Days"
                                className="input w-24"
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-xs text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                                Hours
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={expiryDurationHours}
                                onChange={(e) => setExpiryDurationHours(e.target.value)}
                                placeholder="Hours"
                                className="input w-24"
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-xs text-brand-on-surface dark:text-brand-on-surface-variant mb-1">
                                Minutes
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="59"
                                value={expiryDurationMinutes}
                                onChange={(e) => setExpiryDurationMinutes(e.target.value)}
                                placeholder="Minutes"
                                className="input w-24"
                              />
                            </div>
                          </div>
                          <div className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                            Announcement will expire after the specified duration from now.
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <button
                  onClick={sendAnnouncement}
                  disabled={!announcement.trim()}
                  className="btn btn-primary w-full"
                >
                  Send Announcement
                </button>
              </div>
            </div>

            {/* Render Announcements */}
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-brand-on-background dark:text-brand-on-primary mb-4">
                Current Announcements
              </h2>
              {announcements.length === 0 ? (
                <p className="text-brand-on-surface/50 dark:text-brand-on-surface-variant text-sm">
                  No announcements found.
                </p>
              ) : (
                <div className="space-y-4">
                  {announcements.map((announcement) => (
                    <div
                      key={announcement.id}
                      className="bg-brand-background dark:bg-brand-surface-variant rounded-lg p-4 shadow"
                    >
                      <h3 className="text-md font-semibold text-brand-on-background dark:text-brand-on-primary">
                        {announcement.message}
                      </h3>
                      <p className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant mt-1">
                        Type: {announcement.type}
                      </p>
                      {announcement.expiresAt && (
                        <p className="text-xs text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                          Expires At: {new Date(announcement.expiresAt).toLocaleString()}
                        </p>
                      )}
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => revokeAnnouncement(announcement.id)}
                          className="btn btn-danger btn-sm"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'invites' && (
          <div className="space-y-6">
            {/* Header with Generate Code button */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-brand-on-surface-variant dark:text-brand-on-surface-variant">
                Invite Codes
              </h2>
              <div className="flex gap-4">
                <button
                  onClick={() => generateInviteCode()}
                  className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-brand-on-primary rounded-lg transition-all duration-300 shadow-lg hover:shadow-green-500/20"
                >
                  Generate Code
                </button>
                <button
                  onClick={() => revokeAllCodes()}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-brand-on-primary rounded-lg transition-all duration-300 shadow-lg hover:shadow-red-500/20"
                >
                  Revoke All Codes
                </button>
              </div>
            </div>

            {/* Invite Codes List */}
            <div className="glass-effect backdrop-blur-sm rounded-xl border border-brand-primary/20 overflow-hidden shadow-xl">
              <table className="w-full">
                <thead className="bg-brand-surface border-b border-brand-primary/20">
                  <tr>
                    <th className="text-left px-6 py-4 text-brand-on-surface-variant dark:text-brand-on-surface-variant font-semibold">
                      Code
                    </th>
                    <th className="text-left px-6 py-4 text-brand-on-surface-variant dark:text-brand-on-surface-variant font-semibold">
                      Type
                    </th>
                    <th className="text-left px-6 py-4 text-brand-on-surface-variant dark:text-brand-on-surface-variant font-semibold">
                      Status
                    </th>
                    <th className="text-left px-6 py-4 text-brand-on-surface-variant dark:text-brand-on-surface-variant font-semibold">
                      Created By
                    </th>
                    <th className="text-left px-6 py-4 text-brand-on-surface-variant dark:text-brand-on-surface-variant font-semibold">
                      Expires At
                    </th>
                    <th className="text-left px-6 py-4 text-brand-on-surface-variant dark:text-brand-on-surface-variant font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {inviteCodes.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-8 text-center text-brand-on-background dark:text-brand-on-background/60"
                      >
                        No invite codes found. Generate one to get started!
                      </td>
                    </tr>
                  ) : (
                    inviteCodes.map((code) => (
                      <tr
                        key={code.id}
                        className="border-b border-brand-primary/10 hover:bg-brand-secondary/20 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 text-brand-on-background dark:text-brand-on-background font-mono">
                          {code.code}
                        </td>
                        <td className="px-6 py-4 text-brand-on-background dark:text-brand-on-background/80">
                          {code.type}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 py-1 rounded text-sm ${
                              code.status === 'active'
                                ? 'bg-green-500/20 text-green-300'
                                : code.status === 'used'
                                  ? 'bg-blue-500/20 text-blue-300'
                                  : code.status === 'revoked'
                                    ? 'bg-red-500/20 text-red-300'
                                    : 'bg-brand-outline/20 text-brand-on-surface-variant'
                            }`}
                          >
                            {code.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-brand-on-background dark:text-brand-on-background/80">
                          {code.creator?.username || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 text-brand-on-background dark:text-brand-on-background/80">
                          {new Date(code.expiresAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          {code.status === 'active' && (
                            <button
                              onClick={() => revokeCode(code.id)}
                              className="px-3 py-1 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded transition-colors duration-200"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-8">
            {/* Live Infrastructure Services Overview */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-brand-on-background dark:text-[#e0e7ff] flex items-center gap-2">
                    <span>⚙️</span> Server Infrastructure Services
                  </h2>
                  <p className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                    Live health status and diagnostic probes for core server components
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fetchServicesOverview}
                  disabled={loadingServices}
                  className="btn btn-outlined text-xs px-3 py-1.5 rounded-full border border-brand-outline-variant hover:bg-white/10 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <span className={loadingServices ? 'animate-spin' : ''}>🔄</span> Refresh Status
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* PostgreSQL Database */}
                <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-5 border border-brand-outline-variant/60 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>🐘</span> PostgreSQL Database
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                        servicesOverview?.database?.status === 'healthy'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'bg-red-500/20 text-red-400 border border-red-500/40'
                      }`}
                    >
                      {servicesOverview?.database?.status || 'Active'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] space-y-1">
                    <p>Host: <span className="text-white font-mono">{servicesOverview?.database?.host || 'localhost'}</span></p>
                    <p>Database: <span className="text-white font-mono">{servicesOverview?.database?.database || 'chatdb'}</span></p>
                    <p>Latency: <span className="text-emerald-400 font-semibold">{servicesOverview?.database?.latencyMs ?? 1} ms</span></p>
                  </div>
                </div>

                {/* Redis Cache */}
                <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-5 border border-brand-outline-variant/60 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>⚡</span> Redis Cache & Pub/Sub
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                        servicesOverview?.redis?.status === 'healthy'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'bg-red-500/20 text-red-400 border border-red-500/40'
                      }`}
                    >
                      {servicesOverview?.redis?.status || 'Active'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] space-y-1">
                    <p>Role: <span className="text-white">Session store & WebSocket broadcast</span></p>
                    <p>Memory: <span className="text-white font-mono">{servicesOverview?.redis?.memory || 'Dynamic'}</span></p>
                  </div>
                </div>

                {/* MinIO Object Storage */}
                <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-5 border border-brand-outline-variant/60 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>🗄️</span> MinIO / S3 Storage
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                        servicesOverview?.storage?.status === 'healthy'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                      }`}
                    >
                      {servicesOverview?.storage?.status || 'Active'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] space-y-1">
                    <p>Bucket: <span className="text-white font-mono">{servicesOverview?.storage?.bucket || 'irus-uploads'}</span></p>
                    <p>SSL: <span className="text-white">{servicesOverview?.storage?.useSSL ? 'Enabled' : 'Disabled'}</span></p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestStorage}
                    disabled={testingStorage}
                    className="w-full mt-2 py-1.5 px-3 rounded-xl bg-brand-surface-variant/60 dark:bg-white/10 hover:bg-white/20 text-brand-on-surface text-xs font-semibold active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>{testingStorage ? '⏳ Probing...' : '🧪 Test Storage Probe'}</span>
                  </button>
                </div>

                {/* Coturn WebRTC STUN/TURN */}
                <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-5 border border-brand-outline-variant/60 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>🔊</span> Coturn WebRTC Voice
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                      {servicesOverview?.turn?.status || 'Active'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] space-y-1">
                    <p>Protocol: <span className="text-emerald-400 font-semibold">DTLS-SRTP E2EE</span></p>
                    <p>TURN: <span className="text-white font-mono truncate block">{servicesOverview?.turn?.turnServer || 'turn:3478'}</span></p>
                  </div>
                </div>

                {/* SMTP Email Service */}
                <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-5 border border-brand-outline-variant/60 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>✉️</span> SMTP Email Service
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                        servicesOverview?.smtp?.status === 'healthy'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                      }`}
                    >
                      {servicesOverview?.smtp?.status || 'Configured'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] space-y-1">
                    <p>Host: <span className="text-white font-mono">{servicesOverview?.smtp?.host || 'localhost'}</span></p>
                    <p>Port: <span className="text-white font-mono">{servicesOverview?.smtp?.port || '587'}</span></p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestSmtp}
                    disabled={testingSmtp}
                    className="w-full mt-2 py-1.5 px-3 rounded-xl bg-brand-surface-variant/60 dark:bg-white/10 hover:bg-white/20 text-brand-on-surface text-xs font-semibold active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>{testingSmtp ? '⏳ Sending...' : '🧪 Send Test Email'}</span>
                  </button>
                </div>

                {/* Web Push VAPID */}
                <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-5 border border-brand-outline-variant/60 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>🔔</span> Web Push (VAPID)
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                      {servicesOverview?.push?.status || 'Active'}
                    </span>
                  </div>
                  <div className="text-xs text-brand-outline dark:text-[#c4b5fd] space-y-1">
                    <p>Keys: <span className="text-white font-mono">{servicesOverview?.push?.publicKey || 'Configured'}</span></p>
                    <p>Security Notices: <span className="text-emerald-400 font-semibold">Enabled</span></p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestPush}
                    disabled={testingPush}
                    className="w-full mt-2 py-1.5 px-3 rounded-xl bg-brand-surface-variant/60 dark:bg-white/10 hover:bg-white/20 text-brand-on-surface text-xs font-semibold active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>{testingPush ? '⏳ Sending...' : '🧪 Send Test Push'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Server Settings Form */}
            <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 sm:p-8 border border-brand-outline-variant/60 shadow-md">
              <div className="border-b border-brand-outline-variant/40 pb-4 mb-6">
                <h3 className="text-lg font-bold text-brand-on-background dark:text-white flex items-center gap-2">
                  <span>🛠️</span> Live Server Settings & Policies
                </h3>
                <p className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                  Configure global server branding, voice engine parameters, storage limits, and security policies
                </p>
              </div>

              <form onSubmit={handleSaveServerSettings} className="space-y-6">
                {/* General Branding */}
                <div>
                  <h4 className="text-sm font-bold text-brand-on-surface uppercase tracking-wider mb-4">
                    1. Server Identity & Rules
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Server Name
                      </label>
                      <input
                        type="text"
                        value={serverSettings.serverName || ''}
                        onChange={(e) => setServerSettings({ ...serverSettings, serverName: e.target.value })}
                        className="input w-full"
                        placeholder="Irus Chat"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Welcome Message
                      </label>
                      <input
                        type="text"
                        value={serverSettings.welcomeMessage || ''}
                        onChange={(e) => setServerSettings({ ...serverSettings, welcomeMessage: e.target.value })}
                        className="input w-full"
                        placeholder="Welcome to our server!"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Server Description
                      </label>
                      <textarea
                        rows={2}
                        value={serverSettings.serverDescription || ''}
                        onChange={(e) => setServerSettings({ ...serverSettings, serverDescription: e.target.value })}
                        className="input w-full"
                        placeholder="A self-hosted community chat server."
                      />
                    </div>
                  </div>
                </div>

                {/* Storage Limits */}
                <div className="border-t border-brand-outline-variant/40 pt-6">
                  <h4 className="text-sm font-bold text-brand-on-surface uppercase tracking-wider mb-4">
                    2. File Storage & Encryption
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Max File Upload Size (MB)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={500}
                        value={serverSettings.maxFileSize || 25}
                        onChange={(e) => setServerSettings({ ...serverSettings, maxFileSize: parseInt(e.target.value) || 25 })}
                        className="input w-full"
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-5">
                      <input
                        type="checkbox"
                        id="enableFileUploads"
                        checked={serverSettings.enableFileUploads !== false}
                        onChange={(e) => setServerSettings({ ...serverSettings, enableFileUploads: e.target.checked })}
                        className="w-4 h-4 rounded text-primary-500"
                      />
                      <label htmlFor="enableFileUploads" className="text-xs font-semibold text-brand-on-surface cursor-pointer">
                        Enable File Uploads
                      </label>
                    </div>
                    <div className="flex items-center gap-3 pt-5">
                      <input
                        type="checkbox"
                        id="enableFileEncryption"
                        checked={serverSettings.enableFileEncryption !== false}
                        onChange={(e) => setServerSettings({ ...serverSettings, enableFileEncryption: e.target.checked })}
                        className="w-4 h-4 rounded text-primary-500"
                      />
                      <label htmlFor="enableFileEncryption" className="text-xs font-semibold text-brand-on-surface cursor-pointer">
                        Enable End-to-End File Encryption
                      </label>
                    </div>
                  </div>
                </div>

                {/* Voice Channel Audio Engine */}
                <div className="border-t border-brand-outline-variant/40 pt-6">
                  <h4 className="text-sm font-bold text-brand-on-surface uppercase tracking-wider mb-4">
                    3. WebRTC Voice Engine & Audio Presets
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Bitrate Mode
                      </label>
                      <select
                        value={serverSettings.voiceBitrateMode || 'AUTO'}
                        onChange={(e) => setServerSettings({ ...serverSettings, voiceBitrateMode: e.target.value })}
                        className="input w-full"
                      >
                        <option value="AUTO">AUTO (Dynamic Adaptive)</option>
                        <option value="LOW">LOW (32 kbps — Minimal Bandwidth)</option>
                        <option value="MEDIUM">MEDIUM (64 kbps — Standard Quality)</option>
                        <option value="HIGH">HIGH (128 kbps — Studio Quality)</option>
                        <option value="VERY_HIGH">VERY_HIGH (256 kbps — Lossless Music)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Default Voice Activation Mode
                      </label>
                      <select
                        value={serverSettings.voiceActivationMode || 'VOICE_ACTIVITY'}
                        onChange={(e) => setServerSettings({ ...serverSettings, voiceActivationMode: e.target.value })}
                        className="input w-full"
                      >
                        <option value="VOICE_ACTIVITY">Voice Activity (VAD)</option>
                        <option value="PUSH_TO_TALK">Push-to-Talk</option>
                        <option value="ALWAYS_ON">Always On</option>
                      </select>
                    </div>
                    <div className="space-y-2 pt-2">
                      <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serverSettings.voiceNoiseSuppressionEnabled !== false}
                          onChange={(e) => setServerSettings({ ...serverSettings, voiceNoiseSuppressionEnabled: e.target.checked })}
                          className="w-4 h-4 rounded text-primary-500"
                        />
                        Noise Suppression Enabled
                      </label>
                      <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                        <input
                          type="checkbox"
                          checked={serverSettings.voiceEchoCancellationEnabled !== false}
                          onChange={(e) => setServerSettings({ ...serverSettings, voiceEchoCancellationEnabled: e.target.checked })}
                          className="w-4 h-4 rounded text-primary-500"
                        />
                        Echo Cancellation Enabled
                      </label>
                    </div>
                  </div>
                </div>

                {/* Security & Lockouts */}
                <div className="border-t border-brand-outline-variant/40 pt-6">
                  <h4 className="text-sm font-bold text-brand-on-surface uppercase tracking-wider mb-4">
                    4. Security & Account Protection Policies
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Password Reset Expiration (Minutes)
                      </label>
                      <input
                        type="number"
                        min={5}
                        max={1440}
                        value={serverSettings.passwordResetExpirationMinutes || 60}
                        onChange={(e) => setServerSettings({ ...serverSettings, passwordResetExpirationMinutes: parseInt(e.target.value) || 60 })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Invite Code Expiration (Hours)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={720}
                        value={serverSettings.inviteCodeExpirationHours || 24}
                        onChange={(e) => setServerSettings({ ...serverSettings, inviteCodeExpirationHours: parseInt(e.target.value) || 24 })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-brand-on-surface-variant mb-1">
                        Failed Login Lockout Duration (Minutes)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={1440}
                        value={serverSettings.inviteCodeLockoutDurationMinutes || 30}
                        onChange={(e) => setServerSettings({ ...serverSettings, inviteCodeLockoutDurationMinutes: parseInt(e.target.value) || 30 })}
                        className="input w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={savingSettings}
                    className="btn btn-primary px-8 py-3 rounded-full font-bold shadow-lg shadow-amber-500/25 active:scale-95 transition-all cursor-pointer flex items-center gap-2"
                  >
                    <span>{savingSettings ? '⏳ Saving...' : '💾 Save Server Configuration'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'migration' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold text-brand-on-background dark:text-[#e0e7ff] flex items-center gap-2">
                <span>📦</span> Server Backup & Physical Migration
              </h2>
              <p className="text-xs text-brand-outline dark:text-[#c4b5fd]">
                Export and import complete server databases, channels, accounts, and configurations between physical or cloud hosts
              </p>
            </div>

            {/* Quick Export / Import Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Export Card */}
              <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md space-y-4 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="text-base font-bold text-brand-on-background dark:text-white flex items-center gap-2">
                    <span>📤</span> Export Server Bundle (.json)
                  </h3>
                  <p className="text-xs text-brand-outline dark:text-[#c4b5fd] leading-relaxed">
                    Downloads a self-contained migration archive containing all server settings, user accounts, password hashes, channels, roles, and permissions.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleExportServerData}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <span>📦</span> Download Server Migration Export
                </button>
              </div>

              {/* Import Card */}
              <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 border border-brand-outline-variant/60 shadow-md space-y-4 flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="text-base font-bold text-brand-on-background dark:text-white flex items-center gap-2">
                    <span>📥</span> Import Server Bundle (.json)
                  </h3>
                  <p className="text-xs text-brand-outline dark:text-[#c4b5fd] leading-relaxed">
                    Upload a previously exported JSON bundle to immediately populate this server with all users, channels, roles, and configuration.
                  </p>
                </div>
                <div>
                  <label className="w-full py-3 rounded-2xl bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-500 hover:to-indigo-500 text-white font-bold text-sm shadow-md shadow-primary-600/20 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2">
                    <span>{importingData ? '⏳ Restoring...' : '📂 Choose JSON Bundle to Import'}</span>
                    <input
                      type="file"
                      accept=".json"
                      disabled={importingData}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImportServerData(file);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Full Database Raw SQL Migration (Admin Only) */}
            <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 sm:p-8 border border-amber-500/30 shadow-md space-y-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-base font-bold text-brand-on-background dark:text-white flex items-center gap-2">
                    <span className="text-amber-400">🛡️</span> Full Database SQL Migration (All Messages, Channels & Tables)
                  </h3>
                  <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1 leading-relaxed">
                    Export or restore the entire raw PostgreSQL relational database (<code className="text-white">.sql</code>). Captures 100% of data including message history, threads, pinned messages, reactions, voice activity records, attachments metadata, user credentials, and server permissions.
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  Admin Only
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                {/* Download SQL Dump */}
                <div className="p-4 rounded-2xl bg-brand-surface-variant/40 dark:bg-black/30 border border-brand-outline-variant/40 space-y-3 flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>💾</span> Download Raw Database Dump (.sql)
                    </h4>
                    <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1">
                      Generates a full PostgreSQL database dump and streams it directly to your browser as a downloadable SQL file.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadSqlDump}
                    disabled={downloadingSql}
                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-bold text-xs shadow-md shadow-amber-600/20 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>{downloadingSql ? '⏳ Generating SQL Dump...' : '📥 Download Full PostgreSQL Dump (.sql)'}</span>
                  </button>
                </div>

                {/* Restore SQL Dump */}
                <div className="p-4 rounded-2xl bg-brand-surface-variant/40 dark:bg-black/30 border border-brand-outline-variant/40 space-y-3 flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-sm text-brand-on-background dark:text-white flex items-center gap-2">
                      <span>🔄</span> Restore Database from .sql File
                    </h4>
                    <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1">
                      Upload an exported SQL dump to restore all tables, rows, messages, and accounts directly on this server.
                    </p>
                  </div>
                  <div>
                    <label className="w-full py-2.5 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-bold text-xs shadow-md shadow-red-600/20 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2">
                      <span>{restoringSql ? '⏳ Restoring Database...' : '📂 Choose .sql File to Restore'}</span>
                      <input
                        type="file"
                        accept=".sql"
                        disabled={restoringSql}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleRestoreSqlDump(file);
                          e.target.value = '';
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* CLI Migration Guide */}
            <div className="bg-brand-surface/80 dark:bg-[#252350]/80 rounded-3xl p-6 sm:p-8 border border-brand-outline-variant/60 shadow-md space-y-6">
              <div>
                <h3 className="text-base font-bold text-brand-on-background dark:text-white flex items-center gap-2">
                  <span>🚀</span> Physical Server-to-Server Migration Runbook (Zero Loss)
                </h3>
                <p className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1">
                  Step-by-step instructions to transfer the complete environment (PostgreSQL DB + MinIO attachments + .env) via CLI
                </p>
              </div>

              <div className="space-y-4 text-xs font-mono">
                {/* Step 1 */}
                <div className="p-4 bg-brand-surface-variant/40 dark:bg-black/30 rounded-2xl border border-brand-outline-variant/40 space-y-2">
                  <p className="font-sans font-bold text-brand-on-surface text-sm">Step 1 — Create full backup on Old Server:</p>
                  <pre className="text-emerald-400 bg-black/40 p-2.5 rounded-xl overflow-x-auto">
                    ./manage.sh backup
                  </pre>
                  <p className="font-sans text-brand-outline dark:text-[#c4b5fd]">
                    Creates a complete timestamped archive in <code className="text-white">backups/irus_backup_YYYYMMDD_HHMMSS.tar.gz</code> containing the DB SQL dump, uploads, and config.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="p-4 bg-brand-surface-variant/40 dark:bg-black/30 rounded-2xl border border-brand-outline-variant/40 space-y-2">
                  <p className="font-sans font-bold text-brand-on-surface text-sm">Step 2 — Transfer archive to New Server:</p>
                  <pre className="text-emerald-400 bg-black/40 p-2.5 rounded-xl overflow-x-auto">
                    scp backups/irus_backup_*.tar.gz user@new-server-ip:/opt/irus/backups/
                  </pre>
                </div>

                {/* Step 3 */}
                <div className="p-4 bg-brand-surface-variant/40 dark:bg-black/30 rounded-2xl border border-brand-outline-variant/40 space-y-2">
                  <p className="font-sans font-bold text-brand-on-surface text-sm">Step 3 — Restore on New Server:</p>
                  <pre className="text-emerald-400 bg-black/40 p-2.5 rounded-xl overflow-x-auto">
                    ./manage.sh restore backups/irus_backup_YYYYMMDD_HHMMSS.tar.gz
                  </pre>
                  <p className="font-sans text-brand-outline dark:text-[#c4b5fd]">
                    Automatically populates .env, restores PostgreSQL database, and restarts the container stack.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Channel Members Modal */}
      {showMemberModal && selectedChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface dark:bg-brand-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-brand-outline-variant dark:border-brand-outline-variant flex items-center justify-between">
              <h2 className="text-xl font-semibold text-brand-on-background dark:text-brand-on-primary">
                Manage Members - {selectedChannel.name}
              </h2>
              <button
                onClick={closeMemberModal}
                className="text-brand-on-surface-variant hover:text-brand-on-surface-variant dark:hover:text-brand-on-surface-variant"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Add Member Section */}
              <div className="bg-brand-background dark:bg-brand-surface-variant rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-brand-on-background dark:text-brand-on-primary mb-3">
                  Add Member
                </h3>
                <div className="flex gap-3">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="input flex-1"
                  >
                    <option value="">Select a user...</option>
                    {users
                      .filter((u) => !channelMembers.some((m) => m.userId === u.id))
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.username}
                        </option>
                      ))}
                  </select>
                  <select
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    className="input flex-1"
                  >
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={addChannelMember}
                    disabled={!selectedUserId}
                    className="btn btn-primary whitespace-nowrap"
                  >
                    Add Member
                  </button>
                </div>
              </div>

              {/* Current Members */}
              <h3 className="text-sm font-semibold text-brand-on-background dark:text-brand-on-primary mb-3">
                Current Members ({channelMembers.length})
              </h3>
              <div className="bg-brand-surface dark:bg-brand-surface rounded-lg border border-brand-outline-variant dark:border-brand-outline-variant overflow-hidden">
                <table className="w-full">
                  <thead className="bg-brand-background dark:bg-brand-surface-variant">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase">
                        Username
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase">
                        Added
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-brand-on-surface/50 dark:text-brand-on-surface-variant uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {channelMembers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-brand-on-surface/50 dark:text-brand-on-surface-variant"
                        >
                          No members in this channel yet
                        </td>
                      </tr>
                    ) : (
                      channelMembers.map((member) => (
                        <tr key={member.id}>
                          <td className="px-4 py-3 text-sm font-medium text-brand-on-background dark:text-brand-on-primary">
                            {member.User.username}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                member.User.status === 'online'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : member.User.status === 'idle'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : member.User.status === 'dnd'
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                      : 'bg-brand-background text-brand-on-background dark:bg-brand-background dark:text-brand-on-surface'
                              }`}
                            >
                              {member.User.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={member.roleId}
                              onChange={(e) => updateMemberRole(member.userId, e.target.value)}
                              className="text-sm border-brand-outline-variant dark:border-brand-outline-variant rounded px-2 py-1 bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary"
                              style={{ color: member.Role.color }}
                            >
                              {availableRoles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 text-sm text-brand-on-surface/50 dark:text-brand-on-surface-variant">
                            {new Date(member.assignedAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() =>
                                removeChannelMember(member.userId, member.User.username)
                              }
                              className="text-brand-error hover:text-red-800 dark:text-brand-error dark:hover:text-red-300"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-brand-outline-variant dark:border-brand-outline-variant flex justify-end">
              <button onClick={closeMemberModal} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return workspaceContent;
  }

  return (
    <div className="flex h-screen bg-brand-surface/90 dark:bg-brand-background/90 glass-effect overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div
        className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isMobile ? 'fixed z-50' : 'relative translate-x-0'}
        w-64 border-r border-brand-outline-variant dark:border-brand-outline flex flex-col
        bg-brand-surface/90 dark:bg-brand-background/90 glass-effect
        transition-transform duration-300 ease-in-out
        h-full flex-shrink-0
      `}
      >
        <div className="flex-1 overflow-hidden">
          <ChannelSidebar
            currentChannelId={null}
            onChannelSelect={(id) => navigate(`/chat?channel=${id}`)}
            onDashboardClick={() => navigate('/chat?dashboard=1')}
          />
        </div>
      </div>
      {workspaceContent}
    </div>
  );
}
